/**
 * Event page translations
 *
 * This file contains translations for the Event page and all its components.
 */

import {EventTranslationType} from './types';

// English translations
export const enEvent: EventTranslationType = {
    event: {
        confirmations: {
            close: "Are you sure you want to close this event? No more expenses can be added.",
            reopen: "Are you sure you want to reopen this event? Participants will be able to add expenses again.",
            closeError: "Failed to close event",
            reopenError: "Failed to reopen event",
            deleteItem: "Are you sure you want to delete this item?"
        },
        error: {
            notFound: "The event you're looking for doesn't exist or has been deleted."
        },
        header: {
            back: "Back",
            status: {
                closed: "Closed",
                active: "Active"
            }
        },
        userSelector: {
            title: "Who are you?",
            subtitle: "Select your name to continue",
            owner: "Event Owner",
            error: "EventId is required"
        },
        userWelcome: {
            roles: {
                owner: "Owner",
                actingAsOwner: "Acting as Owner",
                actingAs: "Acting as",
                participant: "Participant"
            },
            messages: {
                managingExpenses: "You are managing expenses for this participant",
                managingEvent: "You are managing this event"
            },
            buttons: {
                copied: "Copied!",
                copy: "Copy",
                share: "Share",
                edit: "Edit",
                active: "Active",
                close: "Close",
                switch: "Switch"
            }
        },
        tabs: {
            expenses: "Expenses",
            balances: "Balances",
            payments: "Payments"
        },
        expenseSection: {
            summary: "Expense Summary",
            totalExpenses: "Total Expenses",
            sponsorships: "Sponsorships",
            finalExpenses: "Final Expenses",
            entries: "Entries",
            entriesSponsors: "+{{count}} sponsored",
            transfersDone: "Transfers Made",
            participants: "{{count}} participants",
            addExpense: "Add Expense",
            addSponsor: "Add Sponsor",
            addEntry: "Add Entry",
            close: "Close",
            eventClosed: {
                title: "Event Closed",
                message: "No more items can be added to this event"
            },
            recentActivity: "Recent Activity",
            filter: {
                filterBy: "Filter by payer:",
                payer: "Payer",
                all: "All",
                allSponsors: "All Sponsors",
                clearFilter: "Clear",
                involvingMe: "Involving Me",
                createdByMe: "Created by Me",
                paidByMe: "Paid by Me"
            }
        },
        balances: {
            title: "Individual Balances",
            loading: "Loading participant names...",
            status: {
                shouldReceive: "should receive",
                needsToPay: "needs to pay",
                allSettled: "all settled"
            },
            sponsor: "({{amount}} sponsor)",
            shareRatio: "Pays {{ratio}}% of a full share",
            holder: {
                holding: "Holding sponsor money",
                paidFromSponsor: "Covered by sponsor money",
                outOfPocket: "Out of pocket",
                fromMembers: "Collected from members"
            },
            summary: {
                totalExpenses: "Total Expenses",
                avgPerPerson: "Average Per Person",
                topSpender: "Top Spender",
                settled: "Settled",
                owes: "Owes",
                owed: "Owed"
            },
            sorting: {
                label: "Sort by",
                default: "Default",
                balanceHighToLow: "Receives most",
                balanceLowToHigh: "Owes most",
                byName: "Name (A-Z)"
            },
            stats: {
                totalPaid: "Total Paid",
                totalOwed: "Total Owed",
                balance: "Balance",
                contribution: "Contribution"
            }
        },
        payments: {
            title: "Suggested Payments",
            markComplete: "Mark payments as completed",
            loading: "Loading participant names...",
            viewProfile: "View profile",
            autoClose: {
                confirm: "Marking a transfer as done also closes the event: no more expenses can be added or edited. You can reopen it any time. Continue?",
                failed: "The transfer was marked, but the event could not be closed.",
                stuckTitle: "A transfer is marked but the event is still open.",
                stuckMessage: "The first tick should have closed it and did not go through. Close it now so the amounts stop changing.",
                stuckAction: "Close event"
            },
            allSettled: {
                title: "All Settled!",
                message: "No payments needed - everyone is even."
            },
            errors: {
                onlySelected: "You can only modify payment status for the selected participant",
                updateFailed: "Failed to update payment status"
            },
            status: {
                paid: "Paid",
                unpaid: "Unpaid",
                pays: "pays",
                to: "to",
                markAsPaid: "Mark as paid",
                markAsUnpaid: "Mark as unpaid",
                markAs: "Mark as {{status}}"
            },
            strategy: {
                label: "Settlement Method",
                transactions: "{{count}} transaction(s)",
                savings: "Saves {{count}} transaction(s) vs default",
                singleCollector: {
                    name: "Single Collector",
                    description: "Everyone pays/receives through one person",
                    selectCollector: "Choose the collector"
                },
                simplePay: {
                    name: "One Payment Each",
                    description: "Each person only makes one transfer"
                },
                locked: {
                    message: "Someone has already marked a transfer as done, so the settlement method is locked. Changing it now would produce a different set of transfers and lose those marks.",
                    switchTo: "Switch to {{name}}",
                    confirmChange: "Switching to \"{{name}}\" rebuilds the transfer list and clears every \"transferred\" mark in this event. Continue?"
                }
            },
            sorting: {
                label: "Sort",
                default: "Default",
                amountHighToLow: "Amount ↓",
                amountLowToHigh: "Amount ↑",
                byFromName: "Sender A-Z",
                byToName: "Recipient A-Z",
                unpaidFirst: "Unpaid first"
            },
            breakdown: {
                show: "Show details",
                hide: "Hide details",
                title: "Where does this amount come from?",
                joined: "{{name}} joined these",
                paid: "{{name}} paid for these",
                columns: {
                    item: "Item",
                    total: "Total",
                    split: "Split",
                    share: "Share"
                },
                totalOwed: "Total share",
                totalPaid: "Total paid",
                net: "Balance",
                ratioNote: "Some people here pay by ratio, so each item is divided into portions (100% = 1 portion). {{name}} counts as {{ratio}}%: Share = Total ÷ portions × {{ratio}}%.",
                partialNote: "This transfer is only part of {{name}}'s balance — it is split across several transfers.",
                noExpenses: "{{name}} is not part of any expense yet."
            },
            sponsorFund: {
                title: "Sponsorship money",
                description: "{{name}} is a sponsor from outside the group and nobody was recorded as holding the money, so the {{amount}} has to be handed to {{recipient}}, who fronted the bills. Set a holder on the sponsorship if someone is already keeping this cash.",
                beneficiaries: "shared by {{count}} people"
            },
            sponsorFundSection: {
                title: "Sponsorship To Collect",
                description: "sponsor money that still has to be handed over"
            },
            receiverSummary: {
                title: "Your summary",
                incoming: "Coming in",
                incomingHint: "{{count}} transfer(s) to you",
                outgoing: "Going out",
                outgoingHint: "{{count}} transfer(s) from you",
                outgoingNone: "nothing to send",
                paid: "You paid",
                paidHint: "bills you covered",
                share: "Your share",
                shareHint: "your part of the event",
                check: "In − out = {{net}}, which matches paid − share.",
                checkHeld: "In − out = {{net}}, which matches paid − share − the {{held}} of sponsor money you hold."
            },
            transferTo: {
                title: "Transfer to {{name}}",
                noBankInfo: "{{name}} has not added any bank details yet — ask them directly.",
                copy: "Copy account number",
                copied: "Copied",
                qrTitle: "Scan to transfer",
                qrHint: "The code already carries {{amount}} and the note below.",
                note: "Note",
                copyNote: "Copy transfer note",
                saveQr: "Save QR image",
                saveQrHint: "Paying from this phone? Save the image, then open your banking app → Scan QR → pick it."
            },
            sponsor: {
                distribution: {
                    title: "Sponsor Distribution",
                    description: "Distributing surplus to members"
                },
                reimbursement: {
                    title: "Sponsor Reimbursement",
                    description: "Reimbursing actual expenses"
                },
                info: {
                    holder: "Sponsor",
                    coverage: "Coverage",
                    surplus: "+{{amount}} surplus",
                    shortfall: "-{{amount}} shortfall"
                },
                toggle: {
                    title: "Distribute Surplus",
                    returnToFund: "Click to return surplus to fund",
                    distributeToMembers: "Click to distribute surplus to members"
                },
                messages: {
                    coversAllWithDistribution: "Sponsor money covers all expenses with surplus being distributed to members.",
                    coversAllReturnFund: "Sponsor money covers all expenses. Surplus ({{amount}}) returns to fund.",
                    partialCoverage: "Sponsor money covers {{coverage}}% of expenses. Members split the remaining {{shortfall}}."
                }
            },
            member: {
                contributions: {
                    title: "Member Contributions",
                    description: "Members pay their share"
                },
                settlements: {
                    title: "Member Settlements",
                    description: "Direct member-to-member payments"
                }
            }
        },
        editEvent: {
            title: "Edit Event",
            subtitle: "Update event information & add participants",
            form: {
                name: {
                    label: "Event Name",
                    placeholder: "Enter event name",
                    required: "Event name is required"
                },
                description: {
                    label: "Description",
                    optional: "(Optional)",
                    placeholder: "Describe your event"
                },
                existingParticipants: {
                    label: "Existing Participants",
                    owner: "Owner",
                    remove: "Remove participant"
                },
                currentParticipants: {
                    label: "Current Participants ({{count}})",
                    count_one: "{{count}} participant already in this event",
                    count_other: "{{count}} participants already in this event",
                    ownerBadge: "Owner",
                    editNameHint: "Click to edit name",
                    saveName: "Save",
                    removeParticipant: "Remove participant"
                },
                newParticipants: {
                    label: "Add New Participants",
                    paste: "Paste",
                    pasteHint: "Paste names from clipboard",
                    placeholder: {
                        empty: "Type names separated by commas",
                        more: "Add more..."
                    },
                    add: "Add",
                    help: "Press Enter or use commas to separate",
                    count_one: "{{count}} new participant",
                    count_other: "{{count}} new participants"
                },
                participantRemoval: {
                    confirmMessage: "This participant is involved in existing expenses. Removing them will also remove them from all expense records. Do you want to continue?",
                    error: "Failed to remove participant. Please try again."
                },
                shareRatio: {
                    hint: "The % next to each name is their share ratio — set 50% for someone who only pays half a share.",
                    help: "Every expense is split by these ratios instead of evenly: someone at 50% pays half of what someone at 100% pays, and the rest falls on the others. E.g. 3 people share a 250K bill and one is at 50% → they pay 50K, the other two 100K each. Sponsorships are shared out the same way.",
                    inputLabel: "{{name}}'s share ratio"
                }
            },
            buttons: {
                cancel: "Cancel",
                saving: "Saving...",
                save: "Save Changes"
            },
            errors: {
                nameRequired: "Event name is required",
                updateFailed: "Failed to update event"
            }
        },
        sponsorLabels: {
            holder: "Holder:",
            benefit: "Benefit",
        },
        components: {
            userInfoModal: {
                bankName: "Bank Name",
                accountNumber: "Account Number",
                accountHolder: "Account Holder",
                noBankInfo: "No Banking Information",
                noBankInfoMessage: "{{participantName}} hasn't added their banking information yet",
                anonymousUser: "Anonymous User",
                anonymousUserMessage: "This user hasn't registered an account yet",
                bankingInformation: "Banking Information"
            },
            copyNotification: {
                eventLinkCopied: "Event link copied!"
            },
            editExpenseForm: {
                title: "Edit Expense",
                amount: "Amount",
                amountPlaceholder: "Enter amount",
                description: "Description",
                descriptionPlaceholder: "What was this expense for?",
                date: "Date",
                whoPaid: "Who Paid?",
                selectPayer: "Select payer",
                splitBetween: "Split Between",
                selectAll: "Select All",
                unselectAll:"Unselect All",
                savingChanges: "Saving Changes...",
                saveChanges: "Save Changes",
                cancel: "Cancel",
                errors: {
                    fillAllFields: "Please fill in all fields",
                    updateFailed: "Failed to update expense. Please try again."
                }
            },
            editSponsorForm: {
                title: "Edit Sponsorship",
                sponsorName: "Sponsor Name",
                sponsorNamePlaceholder: "e.g., ABC Company, John Doe...",
                amount: "Sponsorship Amount",
                amountPlaceholder: "Enter amount",
                sponsorType: "Sponsor Type",
                externalSponsor: "External sponsor (from outside the group)",
                participantSponsor: "Participant sponsor (group member)",
                hasHolder: "Someone holds the sponsor money",
                whoHoldsMoney: "Who holds the money?",
                selectHolder: "Select holder",
                whoIsSponsor: "Who is sponsoring?",
                selectSponsorPayer: "Select sponsor",
                participantSponsorModeLabel: "Payment mode",
                payShareMode: "Still pay their share",
                payShareModeHint: "The sponsor pays the sponsorship amount, then still pays their own share of expenses like everyone else.",
                coverFirstMode: "Sponsor covers their share first",
                coverFirstModeHint: "The sponsor's amount covers their own share first. If sponsorship > their share, they don't pay extra.",
                beneficiaries: "Beneficiaries",
                allParticipants: "All participants",
                selectSpecificPeople: "Select specific people",
                selectAll: "Select All",
                saving: "Saving...",
                saveChanges: "Save Changes",
                cancel: "Cancel",
                errors: {
                    fillAllFields: "Please fill in all fields and select at least one beneficiary",
                    selectHolder: "Please select who will hold the sponsor money",
                    selectSponsorPayer: "Please select who is sponsoring",
                    updateFailed: "Failed to update sponsorship. Please try again."
                }
            },
            userDropdown: {
                loggedIn: "Logged In",
                anonymousUser: "Anonymous User",
                myEvents: "My Events",
                feedbackManagement: "Feedback Management",
                eventManagement: "Event Management",
                userManagement: "User Management",
                eventHistory: "Event History",
                lightMode: "Light Mode",
                darkMode: "Dark Mode",
                logout: "Logout"
            },
            feedbackModal: {
                signInRequired: "Please sign in to submit feedback",
                fillAllFields: "Please fill in all required fields",
                thankYou: "Thank you for your feedback!",
                helpImprove: "Your feedback helps us improve the app experience.",
                shareYourFeedback: "Share Your Feedback",
                feedbackType: "What type of feedback is this?",
                feedbackTypes: {
                    general: "General",
                    bug: "Bug Report",
                    feature: "Feature Request",
                    ui: "UI/UX"
                },
                rateExperience: "How would you rate your overall experience?",
                subject: "Subject",
                subjectPlaceholder: "Brief summary of your feedback",
                message: "Message",
                messagePlaceholder: "Tell us more about your experience, suggestions, or issues...",
                submitting: "Submitting...",
                submitFeedback: "Submit Feedback",
                cancel: "Cancel"
            },
            expenseForm: {
                title: "Add New Expense",
                amount: "Amount",
                amountPlaceholder: "Enter amount",
                description: "Description",
                descriptionPlaceholder: "What was this expense for?",
                whoPaid: "Who Paid?",
                selectPayer: "Select payer",
                splitBetween: "Split Between",
                selectAll: "Select All",
                unselectAll:"Unselect All",
                adding: "Adding Expense...",
                addExpense: "Add Expense",
                errors: {
                    fillAllFields: "Please fill in all fields",
                    invalidAmount: "Please enter a valid amount greater than 0",
                    addFailed: "Failed to add expense. Please try again."
                },
            },
            sponsorForm: {
                title: "Add Sponsorship",
                backToAddExpense: "Back to Add Expense",
                sponsorName: "Sponsor Name",
                sponsorNamePlaceholder: "Who sponsor?",
                amount: "Sponsorship Amount",
                amountPlaceholder: "Enter amount",
                sponsorType: "Sponsor Type",
                externalSponsor: "External sponsor (from outside the group)",
                participantSponsor: "Participant sponsor (group member)",
                hasHolder: "Someone holds the sponsor money",
                whoHoldsMoney: "Who holds the money?",
                selectHolder: "Select holder",
                whoIsSponsor: "Who is sponsoring?",
                selectSponsorPayer: "Select sponsor",
                participantSponsorModeLabel: "Payment mode",
                payShareMode: "Still pay their share",
                payShareModeHint: "The sponsor pays the sponsorship amount, then still pays their own share of expenses like everyone else.",
                coverFirstMode: "Sponsor covers their share first",
                coverFirstModeHint: "The sponsor's amount covers their own share first. If sponsorship > their share, they don't pay extra.",
                beneficiaries: "Beneficiaries",
                allParticipants: "All participants",
                selectSpecificPeople: "Select specific people",
                selectAll: "Select All",
                adding: "Adding...",
                addSponsorship: "Add Sponsorship",
                errors: {
                    fillAllFields: "Please fill in all fields and select at least one beneficiary",
                    selectHolder: "Please select who will hold the sponsor money",
                    selectSponsorPayer: "Please select who is sponsoring",
                    addFailed: "Failed to add sponsorship. Please try again."
                }
            }
        },
        export: {
            exportCSV: "Export CSV",
            export: "Export",
            downloadCSV: "Download all expenses as CSV file",
            exporting: "Exporting...",
            noExpenses: "No expenses to export",
            noSettlements: "No settlements to export",
            exportFailed: "Export failed. Please try again.",
            exportBalances: "Export Balances",
            exportSettlements: "Export Settlements",
            headers: {
                date: "Date",
                description: "Description",
                amount: "Amount",
                payer: "Payer",
                splitBetween: "Split Between",
                sponsorName: "Sponsor Name",
                sponsorHolder: "Sponsor Holder"
            },
            balanceHeaders: {
                participant: "Participant",
                balance: "Balance",
                status: "Status"
            },
            settlementHeaders: {
                from: "From",
                to: "To",
                amount: "Amount"
            },
            balanceStatus: {
                toReceive: "To Receive",
                toPay: "To Pay",
                settled: "Settled"
            },
            values: {
                sponsor: "Sponsor"
            }
        }
    }
};

// Vietnamese translations
export const viEvent: EventTranslationType = {
    event: {
        confirmations: {
            close: "Bạn có chắc chắn muốn đóng sự kiện này không? Không thể thêm chi phí nữa.",
            reopen: "Bạn có chắc chắn muốn mở lại sự kiện này không? Người tham gia sẽ có thể thêm chi phí lại.",
            closeError: "Không thể đóng sự kiện",
            reopenError: "Không thể mở lại sự kiện",
            deleteItem: "Bạn có chắc chắn muốn xóa mục này không?"
        },
        error: {
            notFound: "Sự kiện bạn đang tìm kiếm không tồn tại hoặc đã bị xóa."
        },
        header: {
            back: "Quay lại",
            status: {
                closed: "Đã Đóng",
                active: "Đang Hoạt Động"
            }
        },
        userSelector: {
            title: "Bạn là ai?",
            subtitle: "Chọn tên của bạn để tiếp tục",
            owner: "Chủ Sự Kiện",
            error: "Yêu cầu ID sự kiện"
        },
        userWelcome: {
            roles: {
                owner: "Chủ Sở Hữu",
                actingAsOwner: "Đang Đóng Vai Chủ Sở Hữu",
                actingAs: "Đang Đóng Vai",
                participant: "Người Tham Gia"
            },
            messages: {
                managingExpenses: "Bạn đang quản lý chi phí cho người tham gia này",
                managingEvent: "Bạn đang quản lý sự kiện này"
            },
            buttons: {
                copied: "Đã Chia Sẻ!",
                copy: "Sao Chép",
                share: "Chia Sẻ",
                edit: "Chỉnh Sửa",
                active: "Kích Hoạt",
                close: "Đóng",
                switch: "Chuyển"
            }
        },
        tabs: {
            expenses: "Chi Phí",
            balances: "Số Dư",
            payments: "Thanh Toán"
        },
        expenseSection: {
            summary: "Tổng Quan Chi Phí",
            totalExpenses: "Tổng Chi Phí",
            sponsorships: "Tài Trợ",
            finalExpenses: "Chi Phí Cuối Cùng",
            entries: "Số Mục",
            entriesSponsors: "+{{count}} tài trợ",
            transfersDone: "Đã Chuyển",
            participants: "{{count}} người tham gia",
            addExpense: "Thêm Chi Phí",
            addSponsor: "Thêm Tài Trợ",
            addEntry: "Thêm Khoản Mới",
            close: "Đóng",
            eventClosed: {
                title: "Sự Kiện Đã Đóng",
                message: "Không thể thêm mục nào vào sự kiện này nữa"
            },
            recentActivity: "Hoạt Động Gần Đây",
            filter: {
                filterBy: "Lọc theo người thanh toán:",
                payer: "Người chi",
                all: "Tất cả",
                allSponsors: "Tất cả Sponsors",
                clearFilter: "Xóa",
                involvingMe: "Có mình",
                createdByMe: "Mình tạo",
                paidByMe: "Mình chi"
            }
        },
        balances: {
            title: "Số Dư Cá Nhân",
            loading: "Đang tải tên người tham gia...",
            status: {
                shouldReceive: "nên nhận",
                needsToPay: "cần trả",
                allSettled: "đã thanh toán hết"
            },
            sponsor: "({{amount}} tài trợ)",
            shareRatio: "Trả {{ratio}}% so với một suất đầy đủ",
            holder: {
                holding: "Đang giữ tiền tài trợ",
                paidFromSponsor: "Trả bằng tiền tài trợ",
                outOfPocket: "Tiền túi cần bỏ thêm",
                fromMembers: "Thành viên đóng thêm"
            },
            summary: {
                totalExpenses: "Tổng Chi Phí",
                avgPerPerson: "Trung Bình Mỗi Người",
                topSpender: "Chi Nhiều Nhất",
                settled: "Đã xong",
                owes: "Cần trả",
                owed: "Cần nhận"
            },
            sorting: {
                label: "Sắp xếp theo",
                default: "Mặc định",
                balanceHighToLow: "Nhận nhiều nhất",
                balanceLowToHigh: "Nợ nhiều nhất",
                byName: "Tên (A-Z)"
            },
            stats: {
                totalPaid: "Đã Chi",
                totalOwed: "Phải Chịu",
                balance: "Số Dư",
                contribution: "Đóng Góp"
            }
        },
        payments: {
            title: "Thanh Toán Đề Xuất",
            markComplete: "Đánh dấu thanh toán là hoàn thành",
            loading: "Đang tải tên người tham gia...",
            viewProfile: "Xem hồ sơ",
            autoClose: {
                confirm: "Đánh dấu đã chuyển khoản sẽ chốt luôn sự kiện: không thêm/sửa khoản chi được nữa. Bạn vẫn có thể mở lại bất cứ lúc nào. Tiếp tục?",
                failed: "Đã đánh dấu chuyển khoản nhưng chưa chốt được sự kiện.",
                stuckTitle: "Đã đánh dấu chuyển khoản nhưng sự kiện chưa chốt.",
                stuckMessage: "Lần đánh dấu đầu tiên lẽ ra đã chốt sự kiện nhưng chưa thành công. Chốt lại ngay để số liệu không thay đổi nữa.",
                stuckAction: "Chốt sự kiện"
            },
            allSettled: {
                title: "Tất Cả Đã Thanh Toán!",
                message: "Không cần thanh toán - mọi người đều hòa vốn."
            },
            errors: {
                onlySelected: "Bạn chỉ có thể sửa đổi trạng thái thanh toán cho người tham gia đã chọn",
                updateFailed: "Không thể cập nhật trạng thái thanh toán"
            },
            status: {
                paid: "Đã Thanh Toán",
                unpaid: "Chưa Thanh Toán",
                pays: "trả",
                to: "cho",
                markAsPaid: "Đánh dấu là đã thanh toán",
                markAsUnpaid: "Đánh dấu là chưa thanh toán",
                markAs: "Đánh dấu là {{status}}"
            },
            strategy: {
                label: "Cách Thanh Toán",
                transactions: "{{count}} giao dịch",
                savings: "Tiết kiệm {{count}} giao dịch so với mặc định",
                singleCollector: {
                    name: "Qua 1 Người",
                    description: "Mọi người chuyển/nhận qua 1 người thu tiền",
                    selectCollector: "Chọn người thu tiền"
                },
                simplePay: {
                    name: "Mỗi Người 1 Lần",
                    description: "Mỗi người chỉ cần chuyển khoản 1 lần duy nhất"
                },
                locked: {
                    message: "Đã có người đánh dấu chuyển khoản nên cách thanh toán bị khóa. Đổi lúc này sẽ ra danh sách chuyển tiền khác và mất hết các dấu đã chuyển.",
                    switchTo: "Đổi sang {{name}}",
                    confirmChange: "Đổi sang \"{{name}}\" sẽ tính lại danh sách chuyển tiền và xóa toàn bộ dấu \"đã chuyển khoản\" của sự kiện này. Bạn có chắc không?"
                }
            },
            sorting: {
                label: "Sắp xếp",
                default: "Mặc định",
                amountHighToLow: "Tiền nhiều → ít",
                amountLowToHigh: "Tiền ít → nhiều",
                byFromName: "Người chuyển A-Z",
                byToName: "Người nhận A-Z",
                unpaidFirst: "Chưa chuyển trước"
            },
            breakdown: {
                show: "Xem chi tiết",
                hide: "Ẩn chi tiết",
                title: "Số tiền này ở đâu ra?",
                joined: "{{name}} tham gia các khoản",
                paid: "{{name}} đã trả các khoản",
                columns: {
                    item: "Khoản",
                    total: "Tổng",
                    split: "Chia",
                    share: "Phần"
                },
                totalOwed: "Tổng phải chịu",
                totalPaid: "Tổng đã trả",
                net: "Số dư",
                ratioNote: "Có người trả theo tỉ lệ nên mỗi khoản được chia theo suất (100% = 1 suất). {{name}} tính {{ratio}}%: Phần = Tổng ÷ số suất × {{ratio}}%.",
                partialNote: "Khoản này chỉ là một phần trong số dư của {{name}} — số dư được chia thành nhiều lần chuyển.",
                noExpenses: "{{name}} chưa tham gia khoản chi nào."
            },
            sponsorFund: {
                title: "Tiền tài trợ",
                description: "{{name}} là nhà tài trợ ngoài nhóm và chưa ghi nhận ai đang giữ tiền, nên {{amount}} cần được chuyển cho {{recipient}} — người đã ứng tiền trả bill. Nếu đã có người giữ khoản này, hãy gán người giữ tiền cho mục tài trợ.",
                beneficiaries: "chia cho {{count}} người"
            },
            sponsorFundSection: {
                title: "Tiền Tài Trợ Cần Thu",
                description: "tiền tài trợ chưa được chuyển vào"
            },
            receiverSummary: {
                title: "Tóm tắt của bạn",
                incoming: "Nhận vào",
                incomingHint: "{{count}} lượt chuyển cho bạn",
                outgoing: "Chuyển đi",
                outgoingHint: "{{count}} lượt bạn chuyển đi",
                outgoingNone: "bạn không cần chuyển cho ai",
                paid: "Đã chi",
                paidHint: "các khoản bạn đã trả",
                share: "Được chia",
                shareHint: "phần của bạn trong sự kiện",
                check: "Nhận vào − chuyển đi = {{net}}, khớp với đã chi − được chia.",
                checkHeld: "Nhận vào − chuyển đi = {{net}}, khớp với đã chi − được chia − {{held}} tiền tài trợ bạn đang giữ."
            },
            transferTo: {
                title: "Chuyển tới {{name}}",
                noBankInfo: "{{name}} chưa điền thông tin ngân hàng — bạn hỏi trực tiếp nhé.",
                copy: "Sao chép số tài khoản",
                copied: "Đã chép",
                qrTitle: "Quét mã để chuyển",
                qrHint: "Mã đã có sẵn {{amount}} và nội dung bên dưới.",
                note: "Nội dung",
                copyNote: "Sao chép nội dung chuyển khoản",
                saveQr: "Lưu ảnh QR",
                saveQrHint: "Chuyển bằng chính máy này? Lưu ảnh rồi mở app ngân hàng → Quét QR → chọn ảnh vừa lưu."
            },
            sponsor: {
                distribution: {
                    title: "Phân Phối Tài Trợ",
                    description: "Chia tiền dư cho thành viên"
                },
                reimbursement: {
                    title: "Hoàn Trả Tài Trợ",
                    description: "Hoàn trả chi phí thực tế"
                },
                info: {
                    holder: "Nhà tài trợ",
                    coverage: "Phủ sóng",
                    surplus: "+{{amount}} dư",
                    shortfall: "-{{amount}} thiếu"
                },
                toggle: {
                    title: "Chia Tiền Dư",
                    returnToFund: "Nhấn để trả tiền dư về quỹ",
                    distributeToMembers: "Nhấn để chia tiền dư cho thành viên"
                },
                messages: {
                    coversAllWithDistribution: "Tiền tài trợ đủ chi trả tất cả chi phí và tiền dư sẽ được chia cho thành viên.",
                    coversAllReturnFund: "Tiền tài trợ đủ chi trả tất cả chi phí. Tiền dư ({{amount}}) sẽ được trả về quỹ.",
                    partialCoverage: "Tiền tài trợ chi trả {{coverage}}% chi phí. Thành viên chia nhau {{shortfall}} còn lại."
                }
            },
            member: {
                contributions: {
                    title: "Đóng Góp Thành Viên",
                    description: "Thành viên trả phần của mình"
                },
                settlements: {
                    title: "Thanh Toán Thành Viên",
                    description: "Thanh toán trực tiếp giữa thành viên"
                }
            }
        },
        editEvent: {
            title: "Chỉnh Sửa Sự Kiện",
            subtitle: "Cập nhật thông tin sự kiện & thêm người tham gia",
            form: {
                name: {
                    label: "Tên Sự Kiện",
                    placeholder: "Nhập tên sự kiện",
                    required: "Yêu cầu tên sự kiện"
                },
                description: {
                    label: "Mô Tả",
                    optional: "(Tùy chọn)",
                    placeholder: "Mô tả sự kiện của bạn"
                },
                existingParticipants: {
                    label: "Người Tham Gia Hiện Có",
                    owner: "Chủ sự kiện",
                    remove: "Xóa người tham gia"
                },
                currentParticipants: {
                    label: "Người Tham Gia Hiện Tại ({{count}})",
                    count_other: "{{count}} người tham gia đã có trong sự kiện này",
                    ownerBadge: "Chủ sự kiện",
                    editNameHint: "Bấm để sửa tên",
                    saveName: "Lưu",
                    removeParticipant: "Xoá người tham gia"
                },
                newParticipants: {
                    label: "Thêm Người Tham Gia Mới",
                    paste: "Dán",
                    pasteHint: "Dán danh sách tên từ clipboard",
                    placeholder: {
                        empty: "Nhập tên được phân tách bằng dấu phẩy",
                        more: "Thêm nữa..."
                    },
                    add: "Thêm",
                    help: "Nhấn Enter hoặc sử dụng dấu phẩy để phân tách",
                    count_other: "{{count}} người tham gia mới"
                },
                participantRemoval: {
                    confirmMessage: "Người tham gia này có liên quan đến các chi phí hiện có. Việc xóa họ cũng sẽ xóa họ khỏi tất cả các bản ghi chi phí. Bạn có muốn tiếp tục không?",
                    error: "Không thể xóa người tham gia. Vui lòng thử lại."
                },
                shareRatio: {
                    hint: "Số % cạnh mỗi tên là tỉ lệ chia tiền — để 50% nếu ai đó chỉ trả nửa suất.",
                    help: "Mọi khoản chi sẽ chia theo tỉ lệ thay vì chia đều: người 50% trả bằng một nửa người 100%, phần còn lại do những người khác gánh. VD: 3 người ăn 250K, một người để 50% → người đó trả 50K, hai người còn lại mỗi người 100K. Tiền tài trợ cũng được chia theo tỉ lệ này.",
                    inputLabel: "Tỉ lệ chia tiền của {{name}}"
                }
            },
            buttons: {
                cancel: "Hủy",
                saving: "Đang Lưu...",
                save: "Lưu Thay Đổi"
            },
            errors: {
                nameRequired: "Yêu cầu tên sự kiện",
                updateFailed: "Không thể cập nhật sự kiện"
            }
        },
        sponsorLabels: {
            holder: "Người giữ:",
            benefit: "Thụ hưởng",
        },
        components: {
            userInfoModal: {
                bankName: "Tên Ngân Hàng",
                accountNumber: "Số Tài Khoản",
                accountHolder: "Chủ Tài Khoản",
                noBankInfo: "Không Có Thông Tin Ngân Hàng",
                noBankInfoMessage: "{{participantName}} chưa thêm thông tin ngân hàng của họ",
                anonymousUser: "Người Dùng Ẩn Danh",
                anonymousUserMessage: "Người dùng này chưa đăng ký tài khoản",
                bankingInformation: "Thông Tin Ngân Hàng"
            },
            copyNotification: {
                eventLinkCopied: "Đã sao chép liên kết sự kiện!"
            },
            editExpenseForm: {
                title: "Chỉnh Sửa Chi Phí",
                amount: "Số Tiền",
                amountPlaceholder: "Nhập số tiền",
                description: "Mô Tả",
                descriptionPlaceholder: "Chi phí này dùng cho việc gì?",
                date: "Ngày",
                whoPaid: "Ai Đã Trả?",
                selectPayer: "Chọn người trả",
                splitBetween: "Chia Giữa",
                selectAll: "Chọn Tất Cả",
                unselectAll:"Bỏ Chọn Tất Cả",
                savingChanges: "Đang Lưu Thay Đổi...",
                saveChanges: "Lưu Thay Đổi",
                cancel: "Hủy",
                errors: {
                    fillAllFields: "Vui lòng điền vào tất cả các trường",
                    updateFailed: "Không thể cập nhật chi phí. Vui lòng thử lại."
                },
            },
            editSponsorForm: {
                title: "Chỉnh Sửa Tài Trợ",
                sponsorName: "Tên Nhà Tài Trợ",
                sponsorNamePlaceholder: "Ví dụ: Công ty ABC, Nguyễn Văn A...",
                amount: "Số Tiền Tài Trợ",
                amountPlaceholder: "Nhập số tiền",
                sponsorType: "Loại Tài Trợ",
                externalSponsor: "Tài trợ từ bên ngoài (không phải thành viên nhóm)",
                participantSponsor: "Thành viên trong nhóm tài trợ",
                hasHolder: "Có người giữ tiền tài trợ",
                whoHoldsMoney: "Ai giữ tiền?",
                selectHolder: "Chọn người giữ",
                whoIsSponsor: "Ai tài trợ?",
                selectSponsorPayer: "Chọn người tài trợ",
                participantSponsorModeLabel: "Chế độ thanh toán",
                payShareMode: "Vẫn trả phần của mình",
                payShareModeHint: "Người tài trợ trả tiền tài trợ, sau đó vẫn phải trả phần chi phí của mình như những người khác.",
                coverFirstMode: "Tiền tài trợ trừ vào phần của mình trước",
                coverFirstModeHint: "Tiền tài trợ sẽ trừ vào phần phải trả của người tài trợ trước. Nếu tiền tài trợ > phần phải trả, họ không cần trả thêm.",
                beneficiaries: "Người Thụ Hưởng",
                allParticipants: "Tất cả người tham gia",
                selectSpecificPeople: "Chọn người cụ thể",
                selectAll: "Chọn Tất Cả",
                saving: "Đang Lưu...",
                saveChanges: "Lưu Thay Đổi",
                cancel: "Hủy",
                errors: {
                    fillAllFields: "Vui lòng điền vào tất cả các trường và chọn ít nhất một người thụ hưởng",
                    selectHolder: "Vui lòng chọn ai sẽ giữ tiền tài trợ",
                    selectSponsorPayer: "Vui lòng chọn ai tài trợ",
                    updateFailed: "Không thể cập nhật tài trợ. Vui lòng thử lại."
                }
            },
            userDropdown: {
                loggedIn: "Đã Đăng Nhập",
                anonymousUser: "Người Dùng Ẩn Danh",
                myEvents: "Sự Kiện Của Tôi",
                feedbackManagement: "Quản Lý Phản Hồi",
                eventManagement: "Quản Lý Sự Kiện",
                userManagement: "Quản Lý Người Dùng",
                eventHistory: "Lịch Sử Sự Kiện",
                lightMode: "Chế Độ Sáng",
                darkMode: "Chế Độ Tối",
                logout: "Đăng Xuất"
            },
            feedbackModal: {
                signInRequired: "Vui lòng đăng nhập để gửi phản hồi",
                fillAllFields: "Vui lòng điền vào tất cả các trường bắt buộc",
                thankYou: "Cảm ơn phản hồi của bạn!",
                helpImprove: "Phản hồi của bạn giúp chúng tôi cải thiện trải nghiệm ứng dụng.",
                shareYourFeedback: "Chia Sẻ Phản Hồi Của Bạn",
                feedbackType: "Đây là loại phản hồi gì?",
                feedbackTypes: {
                    general: "Chung",
                    bug: "Báo Cáo Lỗi",
                    feature: "Yêu Cầu Tính Năng",
                    ui: "Giao Diện/UX"
                },
                rateExperience: "Bạn đánh giá trải nghiệm tổng thể của mình như thế nào?",
                subject: "Chủ Đề",
                subjectPlaceholder: "Tóm tắt ngắn gọn về phản hồi của bạn",
                message: "Tin Nhắn",
                messagePlaceholder: "Hãy cho chúng tôi biết thêm về trải nghiệm, đề xuất hoặc vấn đề của bạn...",
                submitting: "Đang Gửi...",
                submitFeedback: "Gửi Phản Hồi",
                cancel: "Hủy"
            },
            expenseForm: {
                title: "Thêm Chi Phí Mới",
                amount: "Số Tiền",
                amountPlaceholder: "Nhập số tiền",
                description: "Mô Tả",
                descriptionPlaceholder: "Chi phí này dùng cho việc gì?",
                whoPaid: "Ai Đã Trả?",
                selectPayer: "Chọn người trả",
                splitBetween: "Chia Giữa",
                selectAll: "Chọn Tất Cả",
                unselectAll: "Bỏ Chọn Tất Cả",
                adding: "Đang Thêm Chi Phí...",
                addExpense: "Thêm Chi Phí",
                errors: {
                    fillAllFields: "Vui lòng điền vào tất cả các trường",
                    invalidAmount: "Vui lòng nhập số tiền hợp lệ lớn hơn 0",
                    addFailed: "Không thể thêm chi phí. Vui lòng thử lại."
                }
            },
            sponsorForm: {
                title: "Thêm Tài Trợ",
                backToAddExpense: "Quay Lại Thêm Chi Phí",
                sponsorName: "Tên Nhà Tài Trợ",
                sponsorNamePlaceholder: "Ai tài trợ?",
                amount: "Số Tiền Tài Trợ",
                amountPlaceholder: "Nhập số tiền",
                sponsorType: "Loại Tài Trợ",
                externalSponsor: "Tài trợ từ bên ngoài (không phải thành viên nhóm)",
                participantSponsor: "Thành viên trong nhóm tài trợ",
                hasHolder: "Có người giữ tiền tài trợ",
                whoHoldsMoney: "Ai giữ tiền?",
                selectHolder: "Chọn người giữ",
                whoIsSponsor: "Ai tài trợ?",
                selectSponsorPayer: "Chọn người tài trợ",
                participantSponsorModeLabel: "Chế độ thanh toán",
                payShareMode: "Vẫn trả phần của mình",
                payShareModeHint: "Người tài trợ trả tiền tài trợ, sau đó vẫn phải trả phần chi phí của mình như những người khác.",
                coverFirstMode: "Tiền tài trợ trừ vào phần của mình trước",
                coverFirstModeHint: "Tiền tài trợ sẽ trừ vào phần phải trả của người tài trợ trước. Nếu tiền tài trợ > phần phải trả, họ không cần trả thêm.",
                beneficiaries: "Người Thụ Hưởng",
                allParticipants: "Tất cả người tham gia",
                selectSpecificPeople: "Chọn người cụ thể",
                selectAll: "Chọn Tất Cả",
                adding: "Đang Thêm...",
                addSponsorship: "Thêm Tài Trợ",
                errors: {
                    fillAllFields: "Vui lòng điền vào tất cả các trường và chọn ít nhất một người thụ hưởng",
                    selectHolder: "Vui lòng chọn ai sẽ giữ tiền tài trợ",
                    selectSponsorPayer: "Vui lòng chọn ai tài trợ",
                    addFailed: "Không thể thêm tài trợ. Vui lòng thử lại."
                }
            }
        },
        export: {
            exportCSV: "Xuất CSV",
            export: "Xuất",
            downloadCSV: "Tải xuống tất cả chi phí dưới dạng file CSV",
            exporting: "Đang xuất...",
            noExpenses: "Không có chi phí để xuất",
            noSettlements: "Không có thanh toán để xuất",
            exportFailed: "Xuất thất bại. Vui lòng thử lại.",
            exportBalances: "Xuất Số Dư",
            exportSettlements: "Xuất Thanh Toán",
            headers: {
                date: "Ngày",
                description: "Mô tả",
                amount: "Số tiền",
                payer: "Người trả",
                splitBetween: "Chia cho",
                sponsorName: "Tên nhà tài trợ",
                sponsorHolder: "Người giữ tiền tài trợ"
            },
            balanceHeaders: {
                participant: "Người tham gia",
                balance: "Số dư",
                status: "Trạng thái"
            },
            settlementHeaders: {
                from: "Từ",
                to: "Đến",
                amount: "Số tiền"
            },
            balanceStatus: {
                toReceive: "Cần nhận",
                toPay: "Cần trả",
                settled: "Đã thanh toán"
            },
            values: {
                sponsor: "Tài trợ"
            }
        }
    }
};